import { Matrix4 } from 'three';

import { assertNever } from '@/helpers/utils';
import { buildTagMatchRegExp } from '@/modules/floorplanDesign/helpers/helpers';

export const combinedMatrix = new Matrix4(
  1.0,
  4.0,
  1.0,
  0.0,
  0.0,
  4.0,
  2.0,
  0.0,
  0.0,
  2.0,
  4.0,
  0.0,
  0.0,
  1.0,
  4.0,
  1.0,
).multiplyScalar(1.0 / 6.0);

export const FDDM_TAG = {
  ORIGIN: 'Origin',

  FLOW: 'Flow',
  FLOW_LOADS_PER_HOUR: 'FlowLoadsPerHour',
  SINGULAR_STATION_AREA: 'SingularStationArea',
  STATIONARY_AREA: 'StationaryArea',
  RACKING_AREA: 'RackingArea',
  HIGHWAY_AREA: 'HighwayArea',
  ANGLED_HIGHWAY_AREA: 'AngledHighwayArea',
  MANUAL_AREA: 'ManualArea',
  OBSTACLE: 'Obstacle',
  WALL: 'Wall',
  LOGICAL_SPACE: 'LogicalSpace',
  LOGICAL_SPACE_TRAFFIC_BOARD_TEXT: 'LogicalSpaceTrafficBoardText',
  LOGICAL_SPACE_TRAFFIC_BOARD: 'LogicalSpaceTrafficBoard',
  TEMPLATE: 'Template',
  GROUP: 'Group',
  FLOW_STOP: 'FlowStop',

  FORKLIFT: 'Forklift',
  RACK: 'Rack',
  HIGHWAY_ARROW: 'HighwayArrow',
  BUBBLE: 'Bubble',
  BUBBLE_ARROW: 'BubbleArrow',
  BUBBLE_DIVIDER: 'BubbleDivider',
  INTER_AREA_CONNECTION: 'InterAreaConnection',
  A_TO_B_CONNECTION: 'aToB',
  B_TO_A_CONNECTION: 'bToA',
  CROSSING: 'Crossing',
  DISTANT_CONNECTION: 'DistantConnection',
  TRACK_PIECE: 'TrackPiece',
  SWEPT_PATH: 'SweptPath',
  POINT_OF_INTEREST: 'Poi',
  POSE: 'Pose',
  STATION: 'Station',
  GATE_MAPPING: 'GateMapping',
  VEHICLE_LAYOUT_POI_CONNECTION: 'VehicleLayoutPoiConnection',
  JIT_POINT: 'JitPoint',
  AREA_CONNECTION_POINT: 'AreaConnectionPoint',

  // reference image
  REFERENCE_IMAGE: 'ReferenceImage',
  // reference image scaling elements
  REFERENCE_SCALING_LINE: 'ReferenceScalingLine',
  REFERENCE_SCALING_POINT_CROSS_LINE_A: 'ReferenceScalingPointACrossLine',
  REFERENCE_SCALING_POINT_CROSS_LINE_B: 'ReferenceScalingPointBCrossLine',

  // 3D
  MODEL: '3D',

  // Layer tags
  LAYER_CONNECTIONS: 'connectionsLayer',
  LAYER_CONNECTION_BUBBLES: 'connectionBubblesLayer',
  LAYER_ROADS: 'roadsLayer',
  LAYER_HIGHWAYS: 'highwaysLayer',
  LAYER_ANGLED_HIGHWAYS: 'angledHighwaysLayer',
  LAYER_AREAS: 'areasLayer',
  LAYER_INTAKE: 'intakeLayer',
  LAYER_DELIVERY: 'deliveryLayer',
  LAYER_STORAGE: 'storageLayer',
  LAYER_CHARGING: 'chargingLayer',
  LAYER_PARKING: 'parkingLayer',
  LAYER_MANUAL: 'manualLayer',
  LAYER_INSERTION: 'insertionLayer',
  LAYER_EXTRACTION: 'extractionLayer',
  LAYER_PROCESS: 'processLayer',
  LAYER_INTERIOR: 'interiorLayer',
  LAYER_WALLS: 'wallsLayer',
  LAYER_OBSTACLES: 'obstaclesLayer',
  LAYER_REFERENCE: 'referenceLayer',
  LAYER_FLOWS: 'flowsLayer',
  LAYER_FLOORPLAN: 'floorplanLayer',
  LAYER_LOGICAL_SPACES: 'logicalSpacesLayer',
  LAYER_GROUPS: 'groupsLayer',
  LAYER_NAMES: 'namesLayer',
  LAYER_AREA_NAMES: 'areaNamesLayer',
  LAYER_HIGHWAY_NAMES: 'highwayNamesLayer',
  LAYER_OBSTACLE_NAMES: 'obstacleNamesLayer',
} as const;

export type Tag = (typeof FDDM_TAG)[keyof typeof FDDM_TAG];

export const FDDM_AREA_TAGS = [
  FDDM_TAG.SINGULAR_STATION_AREA,
  FDDM_TAG.STATIONARY_AREA,
  FDDM_TAG.RACKING_AREA,
  FDDM_TAG.HIGHWAY_AREA,
  FDDM_TAG.MANUAL_AREA,
];

export const INTERACTIVITY_OFF = {
  hoverable: false,
  clickable: false,
  selectable: false,
  rotatable: false,
  draggable: false,
  scalable: false,
  editable: false,
  snappable: false,
};

export const FDDM_EDITABLE_INTERACTIVITY = {
  hoverable: true,
  clickable: true,
  selectable: true,
  rotatable: true,
  draggable: true,
  scalable: true,
  editable: true,
  snappable: true,
};

export const FDDM_SELECT_ONLY_INTERACTIVITY = {
  hoverable: true,
  clickable: false,
  selectable: true,
  rotatable: false,
  draggable: false,
  scalable: false,
  editable: false,
  snappable: false,
};

export const FDDM_CONNECTION_BUBBLE_EDITABLE_INTERACTIVITY = {
  hoverable: false,
  clickable: true,
  selectable: false,
  rotatable: false,
  draggable: false,
  scalable: false,
  editable: false,
  snappable: false,
};

export const FDDM_CONNECTION_BUBBLE_SELECT_ONLY_INTERACTIVITY = {
  hoverable: false,
  clickable: false,
  selectable: false,
  rotatable: false,
  draggable: false,
  scalable: false,
  editable: false,
  snappable: false,
};

export const FDDM_SEGMENT_ELEMENT_EDITABLE_INTERACTIVITY = {
  hoverable: true,
  clickable: false,
  selectable: true,
  rotatable: false,
  draggable: true,
  scalable: false,
  editable: true,
  snappable: true,
};

export const FDDM_SEGMENT_ELEMENT_SELECT_ONLY_INTERACTIVITY = {
  hoverable: true,
  clickable: false,
  selectable: true,
  rotatable: false,
  draggable: false,
  scalable: false,
  editable: false,
  snappable: false,
};

export const INTERACTIVITY_MODE_MAP = {
  EDITABLE: FDDM_EDITABLE_INTERACTIVITY,
  SELECT_ONLY: FDDM_SELECT_ONLY_INTERACTIVITY,
  READONLY: INTERACTIVITY_OFF,
} as const;

export const CONNECTION_BUBBLE_INTERACTIVITY_MODE_MAP = {
  EDITABLE: FDDM_CONNECTION_BUBBLE_EDITABLE_INTERACTIVITY,
  SELECT_ONLY: FDDM_CONNECTION_BUBBLE_SELECT_ONLY_INTERACTIVITY,
  READONLY: INTERACTIVITY_OFF,
} as const;

export const SEGMENT_ELEMENT_INTERACTIVITY_MODE_MAP = {
  EDITABLE: FDDM_SEGMENT_ELEMENT_EDITABLE_INTERACTIVITY,
  SELECT_ONLY: FDDM_SEGMENT_ELEMENT_SELECT_ONLY_INTERACTIVITY,
  READONLY: INTERACTIVITY_OFF,
} as const;

export const TAG_PATTERN_2D_ELEMENTS = buildTagMatchRegExp([
  FDDM_TAG.ORIGIN,
  FDDM_TAG.FORKLIFT,
  FDDM_TAG.HIGHWAY_ARROW,
  FDDM_TAG.RACK,
  FDDM_TAG.FLOW,
  FDDM_TAG.FLOW_LOADS_PER_HOUR,
  FDDM_TAG.SINGULAR_STATION_AREA,
  FDDM_TAG.STATIONARY_AREA,
  FDDM_TAG.RACKING_AREA,
  FDDM_TAG.HIGHWAY_AREA,
  FDDM_TAG.MANUAL_AREA,
  FDDM_TAG.BUBBLE,
  FDDM_TAG.BUBBLE_ARROW,
  FDDM_TAG.BUBBLE_DIVIDER,
  FDDM_TAG.CROSSING,
  FDDM_TAG.POINT_OF_INTEREST,
  FDDM_TAG.STATION,
  FDDM_TAG.GATE_MAPPING,
  FDDM_TAG.TRACK_PIECE,
  FDDM_TAG.SWEPT_PATH,
  FDDM_TAG.OBSTACLE,
  FDDM_TAG.WALL,
  FDDM_TAG.POSE,
]);

export const isFddmTagInteractive = (tag: Tag): boolean => {
  switch (tag) {
    case FDDM_TAG.SINGULAR_STATION_AREA:
    case FDDM_TAG.STATIONARY_AREA:
    case FDDM_TAG.RACKING_AREA:
    case FDDM_TAG.HIGHWAY_AREA:
    case FDDM_TAG.ANGLED_HIGHWAY_AREA:
    case FDDM_TAG.MANUAL_AREA:
    case FDDM_TAG.OBSTACLE:
    case FDDM_TAG.WALL:
    case FDDM_TAG.LOGICAL_SPACE:
    case FDDM_TAG.TEMPLATE:
    case FDDM_TAG.GROUP:
    case FDDM_TAG.BUBBLE:
      return true;

    case FDDM_TAG.FLOW_LOADS_PER_HOUR:
    case FDDM_TAG.INTER_AREA_CONNECTION:
    case FDDM_TAG.POINT_OF_INTEREST:
    case FDDM_TAG.GATE_MAPPING:
    case FDDM_TAG.JIT_POINT:
    case FDDM_TAG.AREA_CONNECTION_POINT:
    case FDDM_TAG.POSE:
    case FDDM_TAG.STATION:
    case FDDM_TAG.ORIGIN:
    case FDDM_TAG.FLOW:
    case FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD_TEXT:
    case FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD:
    case FDDM_TAG.FLOW_STOP:
    case FDDM_TAG.FORKLIFT:
    case FDDM_TAG.RACK:
    case FDDM_TAG.HIGHWAY_ARROW:
    case FDDM_TAG.BUBBLE_ARROW:
    case FDDM_TAG.BUBBLE_DIVIDER:
    case FDDM_TAG.A_TO_B_CONNECTION:
    case FDDM_TAG.B_TO_A_CONNECTION:
    case FDDM_TAG.CROSSING:
    case FDDM_TAG.DISTANT_CONNECTION:
    case FDDM_TAG.TRACK_PIECE:
    case FDDM_TAG.SWEPT_PATH:
    case FDDM_TAG.MODEL:
    case FDDM_TAG.LAYER_CONNECTIONS: // Layer tags
    case FDDM_TAG.LAYER_CONNECTION_BUBBLES:
    case FDDM_TAG.LAYER_ROADS:
    case FDDM_TAG.LAYER_HIGHWAYS:
    case FDDM_TAG.LAYER_ANGLED_HIGHWAYS:
    case FDDM_TAG.LAYER_AREAS:
    case FDDM_TAG.LAYER_INTAKE:
    case FDDM_TAG.LAYER_DELIVERY:
    case FDDM_TAG.LAYER_STORAGE:
    case FDDM_TAG.LAYER_CHARGING:
    case FDDM_TAG.LAYER_PARKING:
    case FDDM_TAG.LAYER_MANUAL:
    case FDDM_TAG.LAYER_INSERTION:
    case FDDM_TAG.LAYER_EXTRACTION:
    case FDDM_TAG.LAYER_PROCESS:
    case FDDM_TAG.LAYER_INTERIOR:
    case FDDM_TAG.LAYER_WALLS:
    case FDDM_TAG.LAYER_OBSTACLES:
    case FDDM_TAG.LAYER_REFERENCE:
    case FDDM_TAG.LAYER_FLOWS:
    case FDDM_TAG.LAYER_FLOORPLAN:
    case FDDM_TAG.LAYER_LOGICAL_SPACES:
    case FDDM_TAG.LAYER_GROUPS:
    case FDDM_TAG.LAYER_NAMES:
    case FDDM_TAG.LAYER_AREA_NAMES:
    case FDDM_TAG.LAYER_HIGHWAY_NAMES:
    case FDDM_TAG.LAYER_OBSTACLE_NAMES:
    case FDDM_TAG.REFERENCE_IMAGE:
    case FDDM_TAG.REFERENCE_SCALING_LINE:
    case FDDM_TAG.REFERENCE_SCALING_POINT_CROSS_LINE_A:
    case FDDM_TAG.REFERENCE_SCALING_POINT_CROSS_LINE_B:
    case FDDM_TAG.VEHICLE_LAYOUT_POI_CONNECTION:
      return false;

    default:
      assertNever(tag);
  }
};

export const FDDM_INTERACTIVE_TAGS = Object.values(FDDM_TAG).filter(isFddmTagInteractive);

export const FDDM_NON_INTERACTIVE_TAGS = Object.values(FDDM_TAG).filter(
  (tag) => !isFddmTagInteractive(tag),
);
